import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@shared/components/ui/button'
import * as Sentry from '@sentry/react'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional fallback UI to render instead of the default error screen */
  fallback?: ReactNode
  /** Called when an error is caught. Use for logging/reporting. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  /** If true, renders a compact inline error instead of a full-page error */
  inline?: boolean
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Global Error Boundary that catches JavaScript errors in its child component tree
 * and renders a fallback UI instead of crashing the entire app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })
    this.props.onError?.(error, errorInfo)

    // Send to Sentry in production
    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      })
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      if (this.props.inline) {
        return <InlineErrorFallback error={this.state.error} onRetry={this.handleReset} />
      }

      return (
        <FullPageErrorFallback
          error={this.state.error}
          onRetry={this.handleReset}
          onReload={this.handleReload}
        />
      )
    }

    return this.props.children
  }
}

function FullPageErrorFallback({
  error,
  onRetry,
  onReload,
}: {
  error: Error | null
  onRetry: () => void
  onReload: () => void
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="mx-auto flex max-w-md flex-col items-center gap-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. You can try again or reload the page.
          </p>
        </div>
        {import.meta.env.DEV && error && (
          <div className="w-full rounded-md border border-border bg-muted/50 p-3 text-left">
            <p className="mb-1 text-xs font-medium text-destructive">{error.name}</p>
            <p className="text-xs text-muted-foreground break-all">{error.message}</p>
          </div>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button onClick={onReload}>
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  )
}

function InlineErrorFallback({
  error,
  onRetry,
}: {
  error: Error | null
  onRetry: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-5 w-5 text-destructive" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">Failed to load this section</p>
        {import.meta.env.DEV && error && (
          <p className="text-xs text-muted-foreground break-all">{error.message}</p>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3 w-3" />
        Retry
      </Button>
    </div>
  )
}

export default ErrorBoundary
